import { useState } from "react";
import {
  Clock3, FileCheck2, ShieldCheck, PhoneCall, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { LeadForm } from "../components/LeadForm";
import { WhatsAppFab } from "../components/WhatsAppFab";

const HERO_IMG = "https://images.unsplash.com/photo-1758598306980-c47a94c6708c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG93bmVyJTIwb2ZmaWNlfGVufDB8fHx8MTc4NzYzMzg0NXww&ixlib=rb-4.1.0&q=85";
const CMA_IMG = "https://images.unsplash.com/photo-1775046318523-b5227f41fe97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG93bmVyJTIwb2ZmaWNlfGVufDB8fHx8MTc4NzYzMzg0NXww&ixlib=rb-4.1.0&q=85";
const TEXTURE_IMG = "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg";

const BENEFITS = [
  { icon: Clock3, title: "2-Minute Check", text: "Answer a few questions about your business and funding requirement. No paperwork needed to start." },
  { icon: FileCheck2, title: "Document Checklist", text: "Get a preliminary document checklist matched to your preferred facility, from Working Capital to NCD." },
  { icon: ShieldCheck, title: "Confidential & Free", text: "Your details stay private with Globus. This initial assessment is completely free, with no obligation." },
  { icon: PhoneCall, title: "Human Review", text: "A Globus funding specialist reviews every request and calls you within one working day." },
];

export default function LandingPage() {
  const [presetPurpose, setPresetPurpose] = useState("");

  const scrollToForm = () => {
    document.getElementById("check")?.scrollIntoView({ behavior: "smooth" });
  };

  const requestCma = () => {
    setPresetPurpose("Detailed CMA Proposal (Paid Service)");
    scrollToForm();
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#F8F9FA]">
      <header className="sticky top-0 z-40 border-b border-slate-200/50 bg-white/70 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1 sm:px-6 lg:px-8">
          <div className="flex items-center" data-testid="brand-logo">
            <img src="/globus-logo.png" alt="Globus Financial Services" data-testid="brand-logo-img"
              className="h-20 sm:h-24 w-auto" />
          </div>
          <div />
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: `url(${TEXTURE_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-8 sm:px-6 md:py-12 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div className="flex flex-col justify-center animate-fade-in-up">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-globus-teal" data-testid="hero-eyebrow">
              Free Loan Readiness Check
            </p>
            <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-globus-navy" data-testid="hero-headline">
              Check Your Business Loan Readiness in 2 Minutes
            </h1>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl" data-testid="hero-subheadline">
              Get a preliminary funding assessment and document checklist from Globus Financial Services.
            </p>
            <ul className="mt-8 flex flex-col gap-3">
              {["Working Capital, Term Loan, LAP, Bill Discounting & NCD / Private Credit",
                "Preliminary assessment reviewed by funding specialists",
                "Document checklist tailored to your facility"].map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-globus-teal" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button data-testid="start-check-button" onClick={scrollToForm} size="lg"
                className="rounded-full bg-globus-navy px-8 text-base text-white hover:bg-globus-navyLight hover:text-white transition-colors duration-200">
                Start Free Check <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-xs text-slate-500">Free. Confidential. No obligation.</p>
            </div>
          </div>

          <div id="check" className="scroll-mt-24 animate-fade-in-up" style={{ animationDelay: "150ms" }}>
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 md:shadow-md" data-testid="form-card">
              <h2 className="font-heading text-xl sm:text-2xl tracking-tight text-globus-navy">Start your free readiness check</h2>
              <p className="mt-1 mb-6 text-sm text-slate-500">Takes about 2 minutes. Our team responds within one working day.</p>
              <LeadForm presetPurpose={presetPurpose} />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="below-form-note">
              Our team will contact you within one working day. This is an initial assessment only—not a loan sanction or final CMA.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8" data-testid="benefits-section">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-globus-teal">Why Globus</p>
        <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-globus-navy max-w-2xl">
          A clearer path to business funding
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5 rounded-xl border border-slate-200 bg-white p-8 shadow-sm md:shadow-md animate-fade-in-up">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-globus-tealLight">
              <Clock3 className="h-6 w-6 text-globus-teal" />
            </div>
            <h3 className="font-heading mt-5 text-xl sm:text-2xl tracking-tight text-globus-navy">{BENEFITS[0].title}</h3>
            <p className="mt-2 text-sm sm:text-base leading-relaxed text-slate-600">{BENEFITS[0].text}</p>
            <img src={HERO_IMG} alt="Business owner reviewing funding options"
              className="mt-6 h-44 w-full rounded-md object-cover max-h-[80vh]" loading="lazy" />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {BENEFITS.slice(1).map((b, i) => (
              <div key={b.title} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm md:shadow-md animate-fade-in-up"
                style={{ animationDelay: `${(i + 1) * 120}ms` }} data-testid={`benefit-card-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-globus-tealLight">
                  <b.icon className="h-6 w-6 text-globus-teal" />
                </div>
                <h3 className="font-heading mt-5 text-lg sm:text-xl tracking-tight text-globus-navy">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
              </div>
            ))}
            <div className="rounded-xl bg-globus-navy p-8 shadow-sm md:shadow-md sm:col-span-2 animate-fade-in-up" style={{ animationDelay: "480ms" }}>
              <h3 className="font-heading text-lg sm:text-xl tracking-tight text-white">Facilities we assess</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Working Capital", "Term Loan", "LAP", "Bill Discounting", "NCD / Private Credit"].map((f) => (
                  <span key={f} className="rounded-full border border-globus-teal/60 px-4 py-1.5 text-xs font-medium text-globus-tealLight">{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-globus-navy" data-testid="cma-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div className="flex flex-col justify-center">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-globus-teal">Paid Service</p>
            <h2 className="font-heading mt-4 text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white" data-testid="cma-headline">
              Need a detailed CMA for bank submission?
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-300">
              Globus prepares professional CMAs, credit notes, and lender-ready funding proposals after review of your requirements.
            </p>
            <div className="mt-8">
              <Button data-testid="request-cma-button" onClick={requestCma} size="lg"
                className="rounded-full bg-white px-8 text-globus-navy hover:bg-globus-tealLight hover:text-globus-navy transition-colors duration-200">
                Request Detailed CMA Proposal <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="relative">
            <img src={CMA_IMG} alt="Globus advisor preparing a CMA report"
              className="h-full max-h-[80vh] w-full rounded-xl object-cover" loading="lazy" />
            <div className="absolute inset-0 rounded-xl bg-globus-navy/10" />
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white" data-testid="footer">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <img src="/globus-logo.png" alt="Globus Financial Services" data-testid="footer-logo-img" className="h-24 w-auto" />
          <p className="mt-2 text-xs tracking-[0.2em] uppercase text-globus-teal font-semibold">Connecting Capital. Creating Growth.</p>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-500" data-testid="disclaimer-text">
            Disclaimer: This is an initial assessment only. It is not a loan sanction, financial advice, or a final CMA.
            Final eligibility is subject to detailed review and lender norms.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            Contact: <a href="mailto:info@globusfinancialservices-in.com" data-testid="footer-email-link"
              className="text-globus-teal transition-colors duration-200 hover:text-globus-tealDark">info@globusfinancialservices-in.com</a>
            {" | "}WhatsApp: <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
              className="text-globus-teal transition-colors duration-200 hover:text-globus-tealDark">+91 87965 12777</a>
          </p>
        </div>
      </footer>

      <WhatsAppFab />
    </div>
  );
}
